"use client"

// Inspired by react-hot-toast library
import * as React from "react"

import type {
  ToastActionElement,
  ToastProps,
} from "@/components/ui/toast"

const TOAST_LIMIT = 1
const TOAST_REMOVE_DELAY = 5000 // 5 seconds

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

type Toast = Omit<ToasterToast, "id">

function toast({ ...props }: Toast) {
  const id = genId()

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ 
      type: "DISMISS_TOAST", 
      ...(toastId && { toastId }) 
    }),
  }
}

// ✅ Enhanced Toast Variant Helpers with better UX
export const toastSuccess = (message: string, title: string = "Success") => toast({
  title,
  description: message,
  variant: "default"
})

export const toastError = (message: string, title: string = "Error") => toast({
  title,
  description: message,
  variant: "destructive"
})

export const toastInfo = (message: string, title: string = "Info") => toast({
  title,
  description: message,
  variant: "default"
})

export const toastWarning = (message: string, title: string = "Warning") => toast({
  title,
  description: message,
  variant: "default"
})

// ✅ ENHANCED: Firebase Error Handler with optional custom options
export const toastFirebaseError = (error: any, options?: {
  title?: string;
  description?: string;
  context?: string;
}) => {
  console.error('🔥 Firebase Error:', error);

  let title = options?.title || "Error";
  let message = options?.description || 'An unexpected error occurred. Please try again.';
  
  // Handle common Firebase errors with context-aware messages
  if (error?.code) {
    switch (error.code) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
      case 'auth/invalid-login-credentials':
        title = "Authentication Failed";
        message = 'Invalid email or password. Please check your credentials and try again.';
        break;
      case 'auth/email-already-in-use':
        title = "Account Exists";
        message = 'An account with this email already exists. Please try logging in instead.';
        break;
      case 'auth/weak-password':
        title = "Weak Password";
        message = 'Password should be at least 8 characters with uppercase, lowercase, numbers, and special characters.';
        break;
      case 'auth/invalid-email':
        title = "Invalid Email";
        message = 'Please enter a valid email address.';
        break;
      case 'auth/too-many-requests':
        title = "Too Many Attempts";
        message = 'Account temporarily locked due to too many failed attempts. Please try again later or reset your password.';
        break;
      case 'auth/network-request-failed':
        title = "Connection Error";
        message = 'Network error. Please check your internet connection and try again.';
        break;
      case 'auth/user-disabled':
        title = "Account Disabled";
        message = 'This account has been disabled. Please contact support for assistance.';
        break;
      case 'auth/requires-recent-login':
        title = "Session Expired";
        message = 'Please log out and log back in to continue with this action.';
        break;
      case 'permission-denied':
        title = "Permission Denied";
        message = options?.context === 'profile' 
          ? 'You do not have permission to access this interpreter profile.'
          : 'You do not have permission to perform this action.';
        break;
      case 'storage/unauthorized':
        title = "Upload Failed";
        message = 'File upload failed. Please ensure you have permission to upload files.';
        break;
      case 'storage/canceled':
        title = "Upload Canceled";
        message = 'File upload was canceled. Please try again.';
        break;
      case 'storage/quota-exceeded':
        title = "Storage Full";
        message = 'Storage quota exceeded. Please try again later or contact support.';
        break;
      case 'storage/invalid-format':
        title = "Invalid File";
        message = 'Invalid file format. Please upload JPEG, PNG, or WebP images only.';
        break;
      case 'app-check/token-error':
      case 'app-check/fetch-network-error':
        title = "Security Verification Failed";
        message = 'Security verification failed. Please refresh the page and try again.';
        break;
      case 'unavailable':
        title = "Service Unavailable";
        message = 'Service temporarily unavailable. Please try again in a few moments.';
        break;
      case 'deadline-exceeded':
        title = "Request Timeout";
        message = 'Request timed out. Please try again with a stable internet connection.';
        break;
      default:
        message = error.message || message;
    }
  } else if (error?.message) {
    message = error.message;
  }

  // ✅ Add context-specific messaging
  if (options?.context) {
    switch (options.context) {
      case 'login':
        title = title === "Error" ? "Login Failed" : title;
        break;
      case 'signup':
        title = title === "Error" ? "Signup Failed" : title;
        break;
      case 'profile':
        title = title === "Error" ? "Profile Error" : title;
        break;
      case 'upload':
        title = title === "Error" ? "Upload Error" : title;
        break;
      case 'token':
        title = title === "Error" ? "Session Error" : title;
        break;
    }
  }

  return toast({
    title,
    description: message,
    variant: "destructive"
  });
}

// ✅ ENHANCED: Talksie Marketplace Specific Helpers
export const toastSignupSuccess = (interpreterName?: string) => toast({
  title: "Welcome to Talksie!",
  description: interpreterName 
    ? `Welcome ${interpreterName}! Your interpreter profile has been created successfully.`
    : "Your account has been created successfully. Welcome to our interpreter marketplace!",
  variant: "default"
})

export const toastLoginSuccess = (firstName?: string, isInterpreter?: boolean) => toast({
  title: "Welcome Back!",
  description: firstName 
    ? `Hello ${firstName}, ${isInterpreter ? "ready to connect with clients?" : "you're successfully logged in."}`
    : "You're successfully logged in to Talksie.",
  variant: "default"
})

export const toastLogoutSuccess = () => toast({
  title: "Logged Out",
  description: "You have been logged out successfully. Thank you for using Talksie!",
  variant: "default"
})

export const toastProfileUpdated = (section?: string) => toast({
  title: "Profile Updated",
  description: section 
    ? `Your ${section} has been updated successfully.`
    : "Your interpreter profile has been updated successfully.",
  variant: "default"
})

// ✅ NEW: App Check and Security Related Toasts
export const toastAppCheckFailed = () => toast({
  title: "Security Check Failed",
  description: "Security verification failed. Please refresh the page and try again.",
  variant: "destructive"
})

export const toastSecurityWarning = (action?: string) => toast({
  title: "Security Warning",
  description: action 
    ? `Suspicious activity detected during ${action}. Please contact support if this continues.`
    : "Suspicious activity detected. Please contact support if this continues.",
  variant: "destructive"
})

// ✅ ENHANCED: Email and Verification Toasts
export const toastEmailVerificationSent = (email?: string) => toast({
  title: "Verification Email Sent",
  description: email 
    ? `Please check ${email} and click the verification link to boost your profile credibility.`
    : "Please check your email and click the verification link.",
  variant: "default"
})

export const toastEmailVerified = () => toast({
  title: "Email Verified!",
  description: "Your email has been verified successfully. This increases your profile credibility with clients.",
  variant: "default"
})

export const toastPasswordResetSent = (email?: string) => toast({
  title: "Password Reset Email Sent",
  description: email 
    ? `Password reset instructions have been sent to ${email}.`
    : "Please check your email for password reset instructions.",
  variant: "default"
})

// ✅ ENHANCED: File Upload Toasts for Profile Pictures
export const toastFileUploadSuccess = (fileName?: string, fileType?: string) => toast({
  title: "Upload Complete",
  description: fileName 
    ? `${fileName} has been uploaded successfully to your interpreter profile.`
    : `${fileType || 'File'} uploaded successfully.`,
  variant: "default"
})

export const toastFileUploadError = (reason?: string, isProfilePicture?: boolean) => toast({
  title: "Upload Failed",
  description: isProfilePicture 
    ? `Profile picture upload failed. ${reason || 'Please ensure your image is under 5MB and in JPEG, PNG, or WebP format.'}`
    : reason || "File upload failed. Please try again.",
  variant: "destructive"
})

export const toastFileValidationError = (issue: string) => toast({
  title: "Invalid File",
  description: `Upload failed: ${issue}. Please select a different file.`,
  variant: "destructive"
})

// ✅ ENHANCED: Interpreter Marketplace Specific Toasts
export const toastProfilePublished = () => toast({
  title: "Profile Live!",
  description: "Your interpreter profile is now live and visible to clients looking for language services.",
  variant: "default"
})

export const toastProfileUnpublished = () => toast({
  title: "Profile Hidden",
  description: "Your profile is now hidden from client searches. You can republish it anytime.",
  variant: "default"
})

export const toastAvailabilityUpdated = (available: boolean) => toast({
  title: available ? "Now Available" : "Set as Unavailable",
  description: available 
    ? "Your profile is now marked as available for new client requests."
    : "Your profile is marked as unavailable. Existing clients can still contact you.",
  variant: "default"
})

export const toastRateUpdated = (newRate: number) => toast({
  title: "Hourly Rate Updated",
  description: `Your hourly rate has been updated to $${newRate}. This applies to new client requests.`,
  variant: "default"
})

// ✅ ENHANCED: Newsletter and Contact Toasts
export const toastNewsletterSubscribed = (email?: string) => toast({
  title: "Subscribed Successfully!",
  description: email 
    ? `${email} has been subscribed to Talksie updates and interpreter marketplace news.`
    : "Thank you for subscribing to our newsletter!",
  variant: "default"
})

export const toastNewsletterError = (reason?: string) => toast({
  title: "Subscription Failed",
  description: reason || "Failed to subscribe to newsletter. Please try again.",
  variant: "destructive"
})

export const toastContactSent = () => toast({
  title: "Message Sent",
  description: "Your message has been sent successfully. Our team will get back to you within 24 hours.",
  variant: "default"
})

export const toastContactError = () => toast({
  title: "Message Failed",
  description: "Failed to send your message. Please try again or contact support directly.",
  variant: "destructive"
})

// ✅ ENHANCED: Utility Toasts
export const toastCopyToClipboard = (text: string = "Text") => toast({
  title: "Copied!",
  description: `${text} copied to clipboard.`,
  variant: "default"
})

export const toastLinkShared = (type?: string) => toast({
  title: "Link Shared",
  description: type 
    ? `${type} link has been copied to your clipboard.`
    : "Link copied to clipboard.",
  variant: "default"
})

// ✅ ENHANCED: AI and Template Generation Toasts
export const toastTemplateGenerated = () => toast({
  title: "Template Generated!",
  description: "Your personalized message template has been created using AI. You can customize it further before sending.",
  variant: "default"
})

export const toastTemplateError = () => toast({
  title: "Generation Failed",
  description: "Failed to generate message template. Please try again or create a custom message.",
  variant: "destructive"
})

export const toastAIQuotaExceeded = () => toast({
  title: "AI Quota Reached",
  description: "You've reached your AI template generation limit. Please try again later or upgrade your plan.",
  variant: "destructive"
})

// ✅ ENHANCED: Configuration for different toast durations
export const toastConfig = {
  durations: {
    success: 4000,
    error: 8000,
    info: 5000,
    warning: 6000,
  },
  
  // Get duration based on variant and importance
  getDuration: (variant: 'default' | 'destructive' = 'default', important?: boolean) => {
    const baseDuration = variant === 'destructive' 
      ? toastConfig.durations.error 
      : toastConfig.durations.success;
    
    return important ? baseDuration + 2000 : baseDuration;
  },

  // Maximum number of toasts to show simultaneously
  maxToasts: TOAST_LIMIT,
  
  // Default removal delay
  removeDelay: TOAST_REMOVE_DELAY
}

// ✅ FIXED: All code paths return a value
export const toastDebug = (message: string, data?: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('🐛 Debug Toast:', message, data);
    return toast({
      title: "Debug",
      description: `${message}${data ? ` - ${JSON.stringify(data)}` : ''}`,
      variant: "default"
    });
  }
  
  // ✅ Return null for production mode (no toast shown)
  return null;
}


export { useToast, toast }
